import React, { useState, useCallback, useRef } from 'react';
import {
  Video,
  Download,
  GraduationCap,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  Presentation
} from 'lucide-react';

const presentations = [
  {
    id: 'kindergarten',
    title: 'Kindergarten Open House',
    titleES: 'Casa Abierta de Kindergarten',
    description: 'Learn about our Kindergarten program for the 2026-2027 school year. This presentation covers curriculum, daily schedule, enrichment activities, and what makes TAG\'s Kindergarten program exceptional.',
    descriptionES: 'Conozca nuestro programa de Kindergarten para el año escolar 2026-2027. Esta presentación cubre el plan de estudios, el horario diario, las actividades de enriquecimiento y lo que hace excepcional al programa de Kindergarten de TAG.',
    file: '/assets/docs/26-27 Kindergarten Open House.pptx',
    icon: GraduationCap,
    color: 'primary',
    year: '2026-2027'
  },
  {
    id: 'middle-school',
    title: 'Middle School Open House',
    titleES: 'Casa Abierta de Escuela Intermedia',
    description: 'Explore our Middle School program for the 2026-2027 school year. This presentation highlights academic offerings, extracurricular activities, and the supportive environment that prepares students for high school success.',
    descriptionES: 'Explore nuestro programa de Escuela Intermedia para el año escolar 2026-2027. Esta presentación destaca las ofertas académicas, las actividades extracurriculares y el ambiente de apoyo que prepara a los estudiantes para el éxito en la escuela secundaria.',
    file: '/assets/docs/26-27  Middle School Open House.pptx',
    icon: BookOpen,
    color: 'gold',
    year: '2026-2027'
  }
];

const VirtualTours = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const tabRefs = useRef([]);

  const currentPresentation = presentations[activeSlide];
  const IconComponent = currentPresentation.icon;

  const handleTabKeyDown = useCallback((e) => {
    let newIndex = activeSlide;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      newIndex = (activeSlide + 1) % presentations.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      newIndex = (activeSlide - 1 + presentations.length) % presentations.length;
    } else if (e.key === 'Home') {
      e.preventDefault();
      newIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      newIndex = presentations.length - 1;
    } else {
      return;
    }
    setActiveSlide(newIndex);
    tabRefs.current[newIndex]?.focus();
  }, [activeSlide]);

  return (
    <main id="main-content" className="bg-white pt-20">
      {/* Hero Section */}
      <section className="section-padding hero-gradient text-white">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 mb-8">
              <Video className="h-5 w-5" aria-hidden="true" />
              <span className="text-sm font-medium tracking-wide uppercase">Virtual Tours</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-light mb-8 leading-tight">
              <span className="block font-extralight text-white/90">Explore</span>
              <span className="block font-medium">TAG School</span>
            </h1>

            <p className="text-xl text-white/90 mb-8 font-light max-w-3xl mx-auto leading-relaxed">
              Take a virtual tour of our programs through these Open House presentations.
              Learn what makes TAG School for Young Scholars a special place for your child.
            </p>
          </div>
        </div>
      </section>

      {/* Presentation Viewer */}
      <section className="section-padding bg-neutral-50" aria-label="Open House Presentations">
        <div className="container-padding">
          <div className="max-w-5xl mx-auto">
            {/* Slide Navigation Tabs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10" role="tablist" aria-label="Select a presentation">
              {presentations.map((pres, index) => {
                const TabIcon = pres.icon;
                return (
                  <button
                    key={pres.id}
                    ref={(el) => (tabRefs.current[index] = el)}
                    role="tab"
                    aria-selected={activeSlide === index}
                    aria-controls={`panel-${pres.id}`}
                    id={`tab-${pres.id}`}
                    tabIndex={activeSlide === index ? 0 : -1}
                    onClick={() => setActiveSlide(index)}
                    onKeyDown={handleTabKeyDown}
                    className={`flex-1 flex items-center space-x-4 p-5 rounded-xl border-2 transition-all duration-300 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 ${
                      activeSlide === index
                        ? pres.color === 'primary'
                          ? 'border-primary-500 bg-primary-50 shadow-lg'
                          : 'border-gold-500 bg-gold-50 shadow-lg'
                        : 'border-neutral-200 bg-white hover:border-neutral-300 hover:shadow-md'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center shrink-0 ${
                      activeSlide === index
                        ? pres.color === 'primary'
                          ? 'bg-primary-500 text-white'
                          : 'bg-gold-500 text-white'
                        : 'bg-neutral-100 text-neutral-500'
                    }`}>
                      <TabIcon className="h-6 w-6" aria-hidden="true" />
                    </div>
                    <div>
                      <div className={`font-medium ${
                        activeSlide === index ? 'text-primary-900' : 'text-neutral-700'
                      }`}>
                        {pres.title}
                      </div>
                      <div className={`text-sm ${
                        activeSlide === index ? 'text-primary-700' : 'text-neutral-600'
                      }`}>
                        {pres.year} School Year
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Active Presentation Panel */}
            {presentations.map((pres, index) => (
              <div
                key={pres.id}
                id={`panel-${pres.id}`}
                role="tabpanel"
                tabIndex={0}
                aria-labelledby={`tab-${pres.id}`}
                hidden={activeSlide !== index}
              >
                {activeSlide === index && (
                  <div className={`${
                    pres.color === 'primary'
                      ? 'bg-primary-50 border-primary-200'
                      : 'bg-gold-50 border-gold-200'
                  } rounded-2xl p-8 md:p-12 border`}>
                    <div className="flex flex-col lg:flex-row gap-8 items-start">
                      {/* Presentation Info */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-6">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                            pres.color === 'primary'
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-gold-100 text-gold-700'
                          }`}>
                            <IconComponent className="h-7 w-7" aria-hidden="true" />
                          </div>
                          <div>
                            <h2 className="text-2xl md:text-3xl font-medium text-primary-900">
                              {pres.title}
                            </h2>
                            <p className={`text-sm font-medium ${
                              pres.color === 'primary' ? 'text-primary-700' : 'text-gold-700'
                            }`}>
                              {pres.year} School Year
                            </p>
                          </div>
                        </div>

                        <p className="text-primary-700 font-light leading-relaxed mb-4">
                          {pres.description}
                        </p>
                        <p lang="es" className="text-primary-700 font-light leading-relaxed text-sm italic mb-8">
                          {pres.descriptionES}
                        </p>

                        {/* Download Button */}
                        <a
                          href={pres.file}
                          download
                          aria-label={`Download ${pres.title} presentation (PowerPoint file)`}
                          className={`inline-flex items-center space-x-3 px-8 py-4 rounded-xl font-medium text-white transition-all duration-300 shadow-lg hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 ${
                            pres.color === 'primary'
                              ? 'bg-primary-600 hover:bg-primary-700'
                              : 'bg-gold-600 hover:bg-gold-700'
                          }`}
                        >
                          <Download className="h-5 w-5" aria-hidden="true" />
                          <span>Download Presentation</span>
                        </a>
                      </div>

                      {/* Visual Card - decorative, hidden from AT */}
                      <div className="w-full lg:w-80 shrink-0" aria-hidden="true">
                        <div className={`rounded-2xl p-8 text-center shadow-lg ${
                          pres.color === 'primary'
                            ? 'bg-primary-700 text-white'
                            : 'bg-gold-700 text-white'
                        }`}>
                          <Presentation className="h-16 w-16 mx-auto mb-4 opacity-90" />
                          <p className="text-xl font-medium mb-2">{pres.title}</p>
                          <p className="text-white/80 text-sm mb-4">{pres.year}</p>
                          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
                            <Download className="h-4 w-4" />
                            <span>PowerPoint File</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Slide Navigation Arrows */}
            <div className="flex items-center justify-between mt-8">
              <button
                onClick={() => setActiveSlide(0)}
                disabled={activeSlide === 0}
                aria-label="Previous presentation"
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 ${
                  activeSlide === 0
                    ? 'text-neutral-400 cursor-not-allowed'
                    : 'text-primary-700 hover:bg-primary-50 border border-primary-200'
                }`}
              >
                <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                <span>Previous</span>
              </button>

              {/* Dots - meets 2.5.8 Target Size with min 24x24 */}
              <div className="flex items-center space-x-3" role="group" aria-label="Presentation indicators">
                {presentations.map((pres, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveSlide(index)}
                    aria-label={`Go to ${pres.title}`}
                    aria-current={activeSlide === index ? 'true' : undefined}
                    className={`min-w-[24px] min-h-[24px] flex items-center justify-center rounded-full transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 ${
                      activeSlide === index
                        ? 'bg-primary-500 w-10'
                        : 'bg-neutral-400 hover:bg-neutral-500'
                    }`}
                  >
                    <span className="sr-only">{pres.title}</span>
                  </button>
                ))}
              </div>

              <button
                onClick={() => setActiveSlide(1)}
                disabled={activeSlide === 1}
                aria-label="Next presentation"
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-medium transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 ${
                  activeSlide === 1
                    ? 'text-neutral-400 cursor-not-allowed'
                    : 'text-primary-700 hover:bg-primary-50 border border-primary-200'
                }`}
              >
                <span>Next</span>
                <ChevronRight className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-white">
        <div className="container-padding">
          <div className="bg-primary-700 rounded-2xl p-8 md:p-12 text-white text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-6">Ready to Join TAG?</h2>
            <p className="text-xl text-white mb-8 font-light max-w-2xl mx-auto">
              After exploring our programs, take the next step and start the admissions process.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/admissions"
                className="bg-white text-primary-900 hover:bg-white/95 font-medium px-10 py-4 rounded-lg transition-all duration-300 shadow-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Apply Now
              </a>
              <a
                href="tel:212-860-6003"
                className="border-2 border-white text-white hover:bg-white/10 font-medium px-10 py-4 rounded-lg transition-all duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                Call Us: 212-860-6003
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default VirtualTours;
